/* Compile-Dialog: Profil wählen, starten, Zustand und Log verfolgen, abbrechen, erneut starten, Ergebnis öffnen.
 * Schließen bricht nichts ab: der Job gehört dem Server. */
import { useEffect, useRef, useState } from 'react';
import type { CloudLink } from '../net/cloud.ts';
import { etaText, parseProgress } from '../net/progress.ts';
import { isReadOnly } from '../net/cloud.ts';
import type { CheckItem } from '../model/types.ts';
import { ENGINES, MAX_THREADS, PROFILES, canRetry, elapsedSec, engineOf, parseThreads, fmtDuration, hasFile, isActive } from '../net/compile.ts';
import type { CompileLink, EngineName, ProfileName } from '../net/compile.ts';
import { Dialog, DialogBody, DialogFoot } from './dialog.tsx';
import { useStore, useT } from './hooks.ts';

export interface CompileHost {
  isBsp(): boolean;
  /** Datei im Editor öffnen (.bsp → Viewer, .pts → Leckanzeige) */
  openFile(file: File): Promise<void>;
  /** Server-Dialog öffnen (Karte speichern) */
  openCloud(): void;
  /** Warteschlange öffnen */
  openQueue(): void;
  /** Hinweise zum Compile-Aufwand der aktuellen Karte (vor dem Start angezeigt) */
  prechecks?(): CheckItem[];
  /** angemeldeter Admin: darf die Threadzahl vorgeben */
  admin?: boolean;
}

const PROFILE_KEYS: Record<ProfileName, string> = { fast: 'compile.p_fast', normal: 'compile.p_normal', final: 'compile.p_final' };
const ENGINE_KEYS: Record<EngineName, string> = { sdhlt: 'compile.e_sdhlt', rust: 'compile.e_rust' };
const STATE_KEYS: Record<string, string> = {
  queued: 'compile.state_queued', running: 'compile.state_running', ok: 'compile.state_ok', failed: 'compile.state_failed',
  cancelled: 'compile.state_cancelled', timeout: 'compile.state_timeout', interrupted: 'compile.state_interrupted'
};
const REASON_KEYS: Record<string, string> = {
  tool_error: 'compile.reason_tool_error', leak: 'compile.reason_leak', oom: 'compile.reason_oom', output_limit: 'compile.reason_output_limit',
  worker_lost: 'compile.reason_worker_lost', worker_stop: 'compile.reason_worker_stop', user: 'compile.reason_user',
  step_timeout: 'compile.reason_step_timeout', job_timeout: 'compile.reason_job_timeout', bad_job: 'compile.reason_bad_job'
};
const STEP_CLASS: Record<string, string> = { ok: 'ok', failed: 'err', running: 'info', pending: 'info', skipped: 'info' };

function useNow(active: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!active) return; const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, [active]);
  return now;
}

export function CompileDialog(props: { compile: CompileLink; cloud: CloudLink; host: CompileHost; onClose: () => void }) {
  const t = useT();
  const { compile, cloud, host } = props;
  const snap = useStore(compile), link = useStore(cloud);
  const [profile, setProfile] = useState<ProfileName>('normal');
  const [engine, setEngine] = useState<EngineName>('sdhlt');
  const [threads, setThreads] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const { job, log } = snap;
  const active = isActive(job);
  const now = useNow(active);
  const logRef = useRef<HTMLPreElement>(null);
  useEffect(() => { const el = logRef.current; if (el) el.scrollTop = el.scrollHeight; }, [log]);

  const prog = active && job && job.state === 'running' ? parseProgress(log) : null;

  const map = link.link;
  const pre = !active && host.prechecks && !host.isBsp() ? host.prechecks() : [];
  const blocked = host.isBsp() ? t('compile.bsp_mode') : !map ? t('compile.no_map') : link.paused ? t('compile.paused') : null;

  const start = async () => {
    if (!map) return;
    setStarting(true); setMsg(t('compile.saving'));
    try {
      // der Server kompiliert den gespeicherten Stand: erst speichern, damit er aktuell ist
      if (!isReadOnly(map) && !(await cloud.save())) { setMsg(t('compile.save_failed')); return; }
      const cur = cloud.getSnapshot().link;
      if (!cur) { setMsg(t('compile.save_failed')); return; }
      setMsg(null);
      await compile.start(cur.id, profile, engine, host.admin ? parseThreads(threads) : undefined);
    } finally { setStarting(false); }
  };
  const cancel = () => { if (job && job.state === 'running' && !window.confirm(t('compile.cancel_confirm'))) return; void compile.cancel(); };
  const openResult = async (name: string) => {
    try { await host.openFile(new File([await compile.file(name)], name)); if (name.endsWith('.bsp')) props.onClose(); }
    catch (e) { setMsg(t('compile.file_failed', { msg: (e as Error).message })); }
  };
  const download = async () => {
    try {
      const url = URL.createObjectURL(new Blob([await compile.file('map.bsp')]));
      const a = document.createElement('a'); a.href = url; a.download = (job ? job.mapName : 'map').replace(/[^\w-]+/g, '_') + '.bsp';
      document.body.append(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { setMsg(t('compile.file_failed', { msg: (e as Error).message })); }
  };

  return (
    <Dialog title={t('compile.title')} onClose={props.onClose}>
      <DialogBody>
        {map && !host.isBsp() ? <p className="small">{t('compile.map', { name: map.name, rev: map.rev })}</p> : null}
        {blocked && <div className="warnbox">{blocked}{!map && !host.isBsp() ? <> <button className="btn sm" onClick={host.openCloud}>{t('compile.open_server')}</button></> : null}</div>}
        {!active && (
          <div className="fields">
            <label htmlFor="xf-compile-profile">{t('compile.profile')}</label>
            <select id="xf-compile-profile" className="sel" value={profile} onChange={(e) => setProfile(e.target.value as ProfileName)}>
              {PROFILES.map((p) => <option key={p} value={p}>{t(PROFILE_KEYS[p])}</option>)}
            </select>
            <label htmlFor="xf-compile-engine">{t('compile.engine')}</label>
            <select id="xf-compile-engine" className="sel" value={engine} onChange={(e) => setEngine(e.target.value as EngineName)}>
              {ENGINES.map((x) => <option key={x} value={x}>{t(ENGINE_KEYS[x])}</option>)}
            </select>
            {host.admin && (
              <>
                <label htmlFor="xf-compile-threads">{t('compile.threads')}</label>
                <input id="xf-compile-threads" className="inp" type="number" min={1} max={MAX_THREADS} step={1} placeholder={t('compile.threads_default')}
                  value={threads} onChange={(e) => setThreads(e.target.value)} />
              </>
            )}
          </div>
        )}
        {!active && engine === 'rust' && <p className="small" data-testid="engine-note">{t('compile.engine_rust_note')}</p>}
        {pre.length > 0 && (
          <div className="warnbox" role="note" data-testid="prechecks">
            <strong>{t('compile.prechecks')}</strong>
            <ul className="checks">{pre.map((c, i) => <li key={i} className={c.level}><span className="pill">{c.level === 'err' ? t('chk.l_err') : t('chk.l_warn')}</span><span>{c.text}</span></li>)}</ul>
          </div>
        )}
        <p className="small">{t('compile.wad_hint')}</p>
        {job && (
          <>
            <div className="row">
              <span className={'pill ' + (job.state === 'ok' ? 'ok' : active ? 'info' : 'err')} style={{ padding: '4px 8px' }} data-state={job.state}>{t(STATE_KEYS[job.state])}</span>
              <span className="small">{job.mapName} · {job.profile} · {t(ENGINE_KEYS[engineOf(job)])}{job.threads ? ' · ' + t('compile.threads_n', { n: job.threads }) : ''}{job.started ? ' · ' + t('compile.elapsed', { time: fmtDuration(elapsedSec(job, now)) }) : ''}</span>
            </div>
            {job.steps && job.steps.length > 0 && (
              <div className="chips" aria-label="steps">
                {job.steps.map((s) => <span key={s.name} className={'chip ' + (STEP_CLASS[s.state] || '') + (job.step === s.name && active ? ' act' : '')} title={s.detail || s.state}>{s.name} · {s.state}</span>)}
              </div>
            )}
            {!active && job.state !== 'ok' && <p className="small" role="status">{(REASON_KEYS[job.reason] ? t(REASON_KEYS[job.reason]) : job.message) + (job.reason !== 'user' && job.message && REASON_KEYS[job.reason] ? ' (' + job.message + ')' : '')}</p>}
            {prog && (
              <div className="prog" data-testid="progress">
                <div className="prog-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={prog.percent} aria-label={t('compile.progress', { step: job.step })}>
                  <span style={{ width: prog.percent + '%' }} />
                </div>
                <span className="small">{prog.done === null || prog.total === null ? t('compile.progress_pct', { step: prog.label || job.step, percent: prog.percent }) : t('compile.progress_text', { step: prog.label || job.step, percent: prog.percent, done: prog.done.toLocaleString(), total: prog.total.toLocaleString() })}{prog.eta ? ' · ' + t('compile.progress_eta', { time: etaText(prog.eta, fmtDuration) }) : ''}</span>
              </div>
            )}
            <h3>{t('compile.log')}</h3>
            <pre className="joblog" ref={logRef} tabIndex={0}>{log || t('compile.log_empty')}</pre>
          </>
        )}
        {(msg || snap.error) && <p className="small" role="alert">{snap.error || msg}</p>}
      </DialogBody>
      <DialogFoot>
        {job && active && <button className="btn" disabled={snap.busy} onClick={cancel}>{t('compile.cancel')}</button>}
        {job && canRetry(job) && <button className="btn" disabled={snap.busy} onClick={() => void compile.retry()}>{t('compile.retry')}</button>}
        {job && hasFile(job, 'map.pts') && <button className="btn" onClick={() => void openResult('map.pts')}>{t('compile.show_leak')}</button>}
        {job && hasFile(job, 'map.bsp') && <button className="btn" onClick={download}>{t('compile.download')}</button>}
        {job && hasFile(job, 'map.bsp') && <button className="btn primary" title={t('compile.open_bsp_tip')} onClick={() => void openResult('map.bsp')}>{t('compile.open_bsp')}</button>}
        <button className="btn" onClick={host.openQueue}>{t('queue.button')}</button>
        {!active && <button className="btn primary" disabled={!!blocked || starting || snap.busy} onClick={() => void start()}>{t('compile.start')}</button>}
        <button className="btn" onClick={props.onClose}>{t('ui.close')}</button>
      </DialogFoot>
    </Dialog>
  );
}
